// Personal insurance policies on the household: types, labels, and the
// anniversary date each year (the policy start date's day and month).
import { nextBirthday } from './bookGrid.js';

export const COVER_TYPES = ['life', 'tpd', 'trauma', 'income_protection', 'other'] as const;
export type CoverType = (typeof COVER_TYPES)[number];
export const COVER_LABEL: Record<CoverType, string> = {
  life: 'Life', tpd: 'TPD', trauma: 'Trauma', income_protection: 'Income protection', other: 'Other cover',
};
export const PREMIUM_FREQUENCIES = ['annual', 'half_yearly', 'quarterly', 'monthly', 'fortnightly'] as const;
export type PremiumFrequency = (typeof PREMIUM_FREQUENCIES)[number];
export const PREMIUM_FREQ_LABEL: Record<PremiumFrequency, string> = {
  annual: 'a year', half_yearly: 'half-yearly', quarterly: 'a quarter', monthly: 'a month', fortnightly: 'a fortnight',
};
const PER_YEAR: Record<PremiumFrequency, number> = { annual: 1, half_yearly: 2, quarterly: 4, monthly: 12, fortnightly: 26 };

export interface InsurancePolicyLike {
  clientId: string;
  coverType: string;
  insurer: string | null;
  lifeInsured: string;
  commencedDate: string | null;
  premiumCents?: number | null;
  premiumFrequency?: string | null;
}

/** Next anniversary on or after `today` (start date's day and month); null without a start date. */
export function nextAnniversary(commenced: string | null | undefined, today: string): string | null {
  if (!commenced) return null;
  // Same rule as a birthday: 29 Feb falls on 28 Feb in common years.
  return nextBirthday(commenced, today);
}

/** Annualised premium in dollars. */
export function annualPremium(p: { premiumCents?: number | null; premiumFrequency?: string | null }): number | null {
  if (p.premiumCents == null) return null;
  const n = PER_YEAR[(p.premiumFrequency ?? 'annual') as PremiumFrequency] ?? 1;
  return (p.premiumCents / 100) * n;
}

export const policyLabel = (p: InsurancePolicyLike): string =>
  `${COVER_LABEL[p.coverType as CoverType] ?? 'Cover'}${p.insurer ? ` (${p.insurer})` : ''}`;

/**
 * Anniversaries falling between today and the horizon, soonest first. Policies for the
 * same person with the same insurer and anniversary are one item ("Life, TPD (TAL) ...").
 */
export function insuranceAnniversaries(policies: InsurancePolicyLike[], today: string, horizon: string): Array<{ clientId: string; date: string; detail: string }> {
  const groups = new Map<string, { clientId: string; date: string; insurer: string | null; who: string; covers: string[] }>();
  for (const p of policies) {
    const d = nextAnniversary(p.commencedDate, today);
    if (!d || d > horizon || p.commencedDate! >= today) continue;
    const who = p.lifeInsured.trim().split(/\s+/)[0] ?? p.lifeInsured;
    const key = `${p.clientId}|${d}|${p.insurer ?? ''}|${p.lifeInsured.trim().toLowerCase()}`;
    const g = groups.get(key) ?? { clientId: p.clientId, date: d, insurer: p.insurer, who, covers: [] };
    const label = COVER_LABEL[p.coverType as CoverType] ?? 'Cover';
    if (!g.covers.includes(label)) g.covers.push(label);
    groups.set(key, g);
  }
  const order = Object.values(COVER_LABEL);
  return [...groups.values()]
    .map((g) => ({
      clientId: g.clientId, date: g.date,
      detail: `${g.covers.sort((a, b) => order.indexOf(a) - order.indexOf(b)).join(', ')}${g.insurer ? ` (${g.insurer})` : ''} anniversary for ${g.who}`,
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.detail.localeCompare(b.detail));
}
